import React from 'react';
import { Grid, styled } from '@mui/material';
import { ChevronRightRounded, PersonAddAlt1, Sync } from '@mui/icons-material';
import { Colors, Text } from 'library';
import { PermissionType, useUserContext } from 'UserContext';
import { useModalsContext } from 'ModalsContext';
import HowItWorkCard from './HowItWorksCard';
import StepTwoImage from 'assets/images/HowItWorksStepTwo.png';
import StepThreeImage from 'assets/images/HowItWorksStepThree.png';

export default function HowItWorks() {
    const { openJobForm } = useModalsContext();
    const { hasPermission } = useUserContext();
    const canPostJobs = hasPermission(PermissionType.PostJob);

    function onPostJobClick(type: 'sub' | 'trial') {
        if (!canPostJobs) {
            return;
        }

        openJobForm('How It Works', {
            frequency: type === 'trial' ? 'recurring' : 'onetime',
            businessJobType: type === 'trial' ? 'TRIAL_RUN' : 'SUBSTITUTE',
        });
    }

    return (
        <Container>
            <Header>
                <Text variant="h1" textStyle={{ marginBottom: 5 }}>
                    The Tandem Process
                </Text>
                <Text variant="caption">
                    Read our{' '}
                    <FaqLink
                        href="https://chatwoot.join-tandem.com/hc/child-care-center-help-center/en"
                        target="_blank"
                        rel="noreferrer"
                    >
                        FAQ
                    </FaqLink>{' '}
                    to learn more.
                </Text>
            </Header>
            <Grid container>
                <HowItWorkCard>
                    <Text variant="body2" bold textStyle={{ color: Colors.turquoise }}>
                        STEP ONE
                    </Text>
                    <Text bold>Post a Job</Text>
                    <Text variant="caption">Tell us what you need and we&apos;ll begin recruiting.</Text>
                    <PostJobButton canPost={canPostJobs} onClick={() => onPostJobClick('sub')}>
                        <Grid container alignItems="center">
                            <span style={{ borderRadius: 25, backgroundColor: Colors.darkNavy, margin: 5 }}>
                                <PersonAddAlt1 style={{ height: 20, width: 20, color: Colors.white, margin: 5 }} />
                            </span>
                            <div>
                                <Text bold>I need a substitute teacher...</Text>
                                <Text variant="caption">
                                    A teacher called off, is on vacation, maternity leave, etc.
                                </Text>
                            </div>
                        </Grid>
                        <ChevronRightRounded
                            htmlColor="inherit"
                            style={{ height: 28, width: 28, color: Colors.darkNavy }}
                        />
                    </PostJobButton>
                    <PostJobButton canPost={canPostJobs} onClick={() => onPostJobClick('trial')}>
                        <Grid container alignItems="center">
                            <span style={{ borderRadius: 25, backgroundColor: Colors.darkNavy, margin: 5 }}>
                                <Sync style={{ height: 20, width: 20, color: Colors.white, margin: 5 }} />
                            </span>
                            <div>
                                <Text bold>I need a permanent teacher...</Text>
                                <Text variant="caption">Vacancy in a classroom, a new role opened, etc.</Text>
                            </div>
                        </Grid>
                        <ChevronRightRounded
                            htmlColor="inherit"
                            style={{ height: 28, width: 28, color: Colors.darkNavy }}
                        />
                    </PostJobButton>
                    <ScheduleCallContainer
                        href="https://meetings.hubspot.com/joy-gupit"
                        target="_blank"
                        rel="noreferrer"
                    >
                        <div>
                            <Text variant="caption" bold textStyle={{ color: Colors.white }}>
                                Have questions around posting a job?
                            </Text>
                            <Text variant="caption" textStyle={{ color: Colors.white }}>
                                Schedule a call with Tandem
                            </Text>
                        </div>
                        <ChevronRightRounded
                            htmlColor="inherit"
                            style={{ height: 28, width: 28, color: Colors.white }}
                        />
                    </ScheduleCallContainer>
                </HowItWorkCard>
                <HowItWorkCard>
                    <Text variant="body2" bold textStyle={{ color: Colors.turquoise }}>
                        STEP TWO
                    </Text>
                    <Text bold>Wait While We Work</Text>
                    <Text variant="caption">
                        We&apos;ll begin recruiting for your role. We&apos;ll place a teacher that fits all the
                        qualifications you&apos;re looking for.
                    </Text>
                    <img
                        src={StepTwoImage}
                        alt="Image showing a worker that is matched up to the requirements in the job posting"
                        style={{ maxWidth: 400 }}
                    />
                </HowItWorkCard>
                <HowItWorkCard>
                    <Text variant="body2" bold textStyle={{ color: Colors.turquoise }}>
                        STEP THREE
                    </Text>
                    <Text bold>Meet Your Match</Text>
                    <Text variant="caption">
                        Once matched, you will receive their info and documents, and they will begin their assignment.
                        If they&apos;re a great fit, you can request them for future openings.
                    </Text>
                    <img
                        src={StepThreeImage}
                        alt="View workers information and downlaod their relevant documents"
                        style={{ maxWidth: 400 }}
                    />
                </HowItWorkCard>
            </Grid>
        </Container>
    );
}

const Container = styled('div')({
    backgroundColor: Colors.lightTurq,
    borderRadius: 10,
    padding: 20,
    marginTop: 20,
});

const Header = styled('div')({
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'column',
    padding: 10,
});

const PostJobButton = styled('button')(({ canPost }: { canPost: boolean }) => ({
    padding: 5,
    alignItems: 'center',
    justifyContent: 'space-between',
    display: 'flex',
    flexDirection: 'row',
    backgroundColor: Colors.lightTurq,
    borderRadius: 10,
    margin: '10px 0',
    boxShadow: '0px 3px 6px #00000029',
    width: '100%',
    textAlign: 'left',
    border: 'none',
    cursor: canPost ? 'pointer' : 'default',
    '&:focus': {
        border: 'none',
    },
}));

const ScheduleCallContainer = styled('a')({
    padding: 10,
    alignItems: 'center',
    justifyContent: 'space-between',
    display: 'flex',
    flexDirection: 'row',
    backgroundColor: Colors.turquoise,
    borderRadius: 10,
    margin: '10px 0',
    boxShadow: '0px 3px 6px #00000029',
    width: '100%',
    marginTop: 'auto',
    '&:hover': {
        textDecoration: 'none',
    },
});

const FaqLink = styled('a')({
    color: Colors.darkNavy,
    textDecoration: 'underline',
    fontWeight: 'bold',
    '&:hover': {
        color: Colors.darkNavy,
    },
});
